import EmberService from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { GenieAnswerReadyDetail } from '@databox/ember-integrations/lib/components/Navigation/navigation.contract';

import { InfolayerItemType } from 'webapp/enums/enums';
import { uniqueId } from 'webapp/utils/common';

import type Board from 'webapp/models-custom/board';
import type { ConstructorData as BaseConstructorData } from 'webapp/models-custom/infolayer-item/base';
import {
	EXPIRE_ERROR,
	EXPIRE_INFO,
	EXPIRE_OK,
	EXPIRE_PROGRESS,
	EXPIRE_WARNING,
} from 'webapp/models-custom/infolayer-item/base';
import type InfolayerItemBase from 'webapp/models-custom/infolayer-item/base';
import InfolayerItemBoardSnapshot, {
	type BoardLikeType,
} from 'webapp/models-custom/infolayer-item/board-snapshot';
import type { DataExportDeliveryLikeType } from 'webapp/models-custom/infolayer-item/data-export';
import InfolayerItemDataExport from 'webapp/models-custom/infolayer-item/data-export';
import type { DatasetSyncType } from 'webapp/models-custom/infolayer-item/dataset-sync';
import InfolayerItemDatasetSync from 'webapp/models-custom/infolayer-item/dataset-sync';
import type {
	ActionButton,
	ConstructorData as GeneralConstructorData,
} from 'webapp/models-custom/infolayer-item/general';
import InfolayerItemGeneral, {
	Type as GeneralType,
} from 'webapp/models-custom/infolayer-item/general';
import InfolayerItemGenieChatEnd from 'webapp/models-custom/infolayer-item/genie-chat-end';
import InfolayerItemLoopPdf, {
	type LoopLikeType,
} from 'webapp/models-custom/infolayer-item/loop-pdf';
import type { ReportDeliveryLikeType } from 'webapp/models-custom/infolayer-item/report-pdf';
import InfolayerItemReportPdf from 'webapp/models-custom/infolayer-item/report-pdf';
import InfolayerItemStatusObjects from 'webapp/models-custom/infolayer-item/status-objects';
import InfolayerItemViewBoard from 'webapp/models-custom/infolayer-item/view-board';
import InfolayerItemViewDigest from 'webapp/models-custom/infolayer-item/view-digest';
import type Scorecard from 'webapp/models-custom/scorecard';
import StatusObject from 'webapp/models-custom/status-object';

export type InfolayerItem =
	| InfolayerItemGeneral
	| InfolayerItemStatusObjects
	| InfolayerItemReportPdf
	| InfolayerItemBoardSnapshot
	| InfolayerItemLoopPdf
	| InfolayerItemViewDigest
	| InfolayerItemViewBoard
	| InfolayerItemDataExport
	| InfolayerItemDatasetSync
	| InfolayerItemGenieChatEnd;

// every field of any of the given types, with the type(s) it has there
type MergedFields<T> = {
	[K in T extends unknown ? keyof T : never]?: T extends Partial<Record<K, infer V>> ? V : never;
};

// what createNew reads: each item type only uses its own fields, which the methods below always pass
type CreateNewData = { expire?: number | null } & MergedFields<
	| ConstructorParameters<typeof InfolayerItemStatusObjects>[0]
	| ConstructorParameters<typeof InfolayerItemReportPdf>[0]
	| ConstructorParameters<typeof InfolayerItemBoardSnapshot>[0]
	| ConstructorParameters<typeof InfolayerItemLoopPdf>[0]
	| ConstructorParameters<typeof InfolayerItemDataExport>[0]
	| ConstructorParameters<typeof InfolayerItemViewDigest>[0]
	| ConstructorParameters<typeof InfolayerItemViewBoard>[0]
	| ConstructorParameters<typeof InfolayerItemDatasetSync>[0]
	| ConstructorParameters<typeof InfolayerItemGenieChatEnd>[0]
	| GeneralConstructorData
>;

export default class InfolayerService extends EmberService {
	@tracked items: InfolayerItem[] = [];

	createNew(type: InfolayerItemType, data: CreateNewData): InfolayerItem {
		const onClose = (item: InfolayerItemBase) => {
			setTimeout(() => {
				this.items = this.items.filter((i) => i !== item);
			}, 1000);
		};

		const commonData: BaseConstructorData = {
			id: data.id || uniqueId(),
		};

		let expire = data.expire || null;
		let infolayerItem;

		switch (type) {
			case InfolayerItemType.STATUS_OBJECTS: {
				const statusObjectData = Object.assign(
					{
						statusObjects: data.statusObjects!,
					},
					commonData,
				);
				infolayerItem = new InfolayerItemStatusObjects(statusObjectData, onClose);
				expire = EXPIRE_ERROR;
				break;
			}
			case InfolayerItemType.REPORT_PDF: {
				const reportPdfData = Object.assign(
					{
						reportDelivery: data.reportDelivery!,
					},
					commonData,
				);
				infolayerItem = new InfolayerItemReportPdf(reportPdfData, onClose);
				break;
			}
			case InfolayerItemType.BOARD_SNAPSHOT: {
				const boardSnapshotData = Object.assign(
					{
						board: data.board!,
					},
					commonData,
				);
				infolayerItem = new InfolayerItemBoardSnapshot(boardSnapshotData, onClose);
				break;
			}
			case InfolayerItemType.LOOP_PDF: {
				const loopPdfData = Object.assign(
					{
						loop: data.loop!,
					},
					commonData,
				);
				infolayerItem = new InfolayerItemLoopPdf(loopPdfData, onClose);
				break;
			}
			case InfolayerItemType.DATA_EXPORT: {
				const dataExport = Object.assign(
					{
						dataExportDelivery: data.dataExportDelivery!,
					},
					commonData,
				);
				infolayerItem = new InfolayerItemDataExport(dataExport, onClose);
				break;
			}
			case InfolayerItemType.VIEW_DIGEST: {
				const digestData = Object.assign(
					{
						metricDigest: data.metricDigest!,
					},
					commonData,
				);
				infolayerItem = new InfolayerItemViewDigest(digestData, onClose);
				break;
			}
			case InfolayerItemType.VIEW_BOARD: {
				const boardData = Object.assign(
					{
						board: data.board as Board, // viewBoard passes a Board
					},
					commonData,
				);
				infolayerItem = new InfolayerItemViewBoard(boardData, onClose);
				break;
			}
			case InfolayerItemType.DATASET_SYNC: {
				const datasetSyncData = Object.assign(
					{
						datasetSync: data.datasetSync!,
					},
					commonData,
				);
				infolayerItem = new InfolayerItemDatasetSync(datasetSyncData, onClose);
				expire = EXPIRE_PROGRESS;
				break;
			}
			case InfolayerItemType.GENIE_CHAT_END: {
				infolayerItem = new InfolayerItemGenieChatEnd(
					{
						threadId: data.threadId!,
						title: data.title!,
						onOpen: data.onOpen!,
						...commonData,
					},
					onClose,
				);
				// no auto-expire: stays until Open chat or Dismiss
				expire = null;
				break;
			}
			default: {
				const generalData = Object.assign(
					{
						text: data.text,
						type: data.type,
					},
					commonData,
				) as GeneralConstructorData;

				if (data.actionButton) {
					generalData.actionButton = data.actionButton;
					expire = EXPIRE_ERROR * 2;
				}

				if (!expire) {
					switch (data.type) {
						case GeneralType.OK:
							expire = EXPIRE_OK;
							break;
						case GeneralType.ERROR:
							expire = EXPIRE_ERROR;
							break;
						case GeneralType.WARNING:
							expire = EXPIRE_WARNING;
							break;
						case GeneralType.INFO:
							expire = EXPIRE_INFO;
							break;
					}
				}
				infolayerItem = new InfolayerItemGeneral(generalData, onClose);
				break;
			}
		}

		infolayerItem.expire(expire);

		if (!infolayerItem.checkIfDuplicated(this.items)) {
			this.items = [...this.items, infolayerItem];
		}

		return infolayerItem;
	}

	getLayerById(id: string): InfolayerItem | null {
		const layersItems = this.items;

		if (layersItems) {
			return layersItems.find((item) => item.id === id) || null;
		}

		return null;
	}

	ok(text: string) {
		const item = this.createNew(InfolayerItemType.GENERAL, {
			text,
			type: GeneralType.OK,
		});
		return item as InfolayerItemGeneral;
	}

	okWithActionButton(text: string, actionButton: ActionButton) {
		const item = this.createNew(InfolayerItemType.GENERAL, {
			text,
			actionButton,
			type: GeneralType.OK,
		});
		return item as InfolayerItemGeneral;
	}

	error(text: string) {
		const item = this.createNew(InfolayerItemType.GENERAL, {
			text,
			type: GeneralType.ERROR,
		});
		return item as InfolayerItemGeneral;
	}

	errorWithActionButton(text: string, actionButton: ActionButton) {
		const item = this.createNew(InfolayerItemType.GENERAL, {
			text,
			actionButton,
			type: GeneralType.ERROR,
		});
		return item as InfolayerItemGeneral;
	}

	warning(text: string) {
		const item = this.createNew(InfolayerItemType.GENERAL, {
			text,
			type: GeneralType.WARNING,
		});
		return item as InfolayerItemGeneral;
	}

	info(text: string) {
		const item = this.createNew(InfolayerItemType.GENERAL, {
			text,
			type: GeneralType.INFO,
		});
		return item as InfolayerItemGeneral;
	}

	loading(text: string, expire?: number) {
		const item = this.createNew(InfolayerItemType.GENERAL, {
			text,
			type: GeneralType.LOADING,
			expire,
		});
		return item as InfolayerItemGeneral;
	}

	statusObjects(statusObjects: StatusObject[]) {
		// We want to remove duplicated status objects, since we want to show only unique toast messages to the user
		const eliminatedDuplicates = StatusObject.eliminateDuplicates(statusObjects);
		const filteredStatusObjects = this.filterOutExistingStatusObjects(eliminatedDuplicates);

		if (!filteredStatusObjects.length) {
			return;
		}

		const item = this.createNew(InfolayerItemType.STATUS_OBJECTS, {
			statusObjects: filteredStatusObjects,
		});

		return item as InfolayerItemStatusObjects;
	}

	reportPdf(reportDelivery: ReportDeliveryLikeType) {
		const item = this.createNew(InfolayerItemType.REPORT_PDF, {
			reportDelivery,
		});
		return item as InfolayerItemReportPdf;
	}

	loopPdf(loop: LoopLikeType) {
		const item = this.createNew(InfolayerItemType.LOOP_PDF, {
			loop,
		});
		return item as InfolayerItemLoopPdf;
	}

	boardSnapshot(board: BoardLikeType) {
		const item = this.createNew(InfolayerItemType.BOARD_SNAPSHOT, {
			board,
		});
		return item as InfolayerItemBoardSnapshot;
	}

	dataExport(dataExportDelivery: DataExportDeliveryLikeType) {
		const item = this.createNew(InfolayerItemType.DATA_EXPORT, {
			dataExportDelivery,
		});
		return item as InfolayerItemDataExport;
	}

	datasetSync(datasetSync: DatasetSyncType) {
		const item = this.createNew(InfolayerItemType.DATASET_SYNC, {
			datasetSync,
		});
		return item as InfolayerItemDatasetSync;
	}

	showGenieAnswerReady(detail: GenieAnswerReadyDetail, onOpen: (threadId: string) => void) {
		this.items
			.filter(
				(item) =>
					item instanceof InfolayerItemGenieChatEnd && item.threadId === detail.threadId,
			)
			.forEach((item) => {
				item.close();
			});
		// close() removes the old item after the fade-out; hide it from the list straight away
		this.items = this.items.filter(
			(item) =>
				!(item instanceof InfolayerItemGenieChatEnd && item.threadId === detail.threadId),
		);

		const item = this.createNew(InfolayerItemType.GENIE_CHAT_END, {
			threadId: detail.threadId,
			title: detail.title,
			onOpen,
		});
		return item as InfolayerItemGenieChatEnd;
	}

	viewDigest(metricDigest: Scorecard | null) {
		const item = this.createNew(InfolayerItemType.VIEW_DIGEST, {
			metricDigest,
		});
		return item as InfolayerItemViewDigest;
	}

	viewBoard(board: Board) {
		const item = this.createNew(InfolayerItemType.VIEW_BOARD, {
			board,
		});
		return item as InfolayerItemViewBoard;
	}

	closeAll(): void {
		this.items.forEach((item) => {
			item.close();
		});
	}

	private filterOutExistingStatusObjects(statusObjects: StatusObject[]): StatusObject[] {
		const uniqueStatusObjects: StatusObject[] = [];

		if (!this.items.length) {
			return statusObjects;
		}

		statusObjects.forEach((statusObject) => {
			const isDuplicate = this.items.some((item) => {
				return (
					item instanceof InfolayerItemStatusObjects &&
					item.statusObjects.some((so) => {
						return so.isEqual(statusObject);
					})
				);
			});

			if (!isDuplicate) {
				uniqueStatusObjects.push(statusObject);
			}
		});

		return uniqueStatusObjects;
	}
}

declare module '@ember/service' {
	interface Registry {
		infolayer: InfolayerService;
	}
}
